import { createFileRoute } from "@tanstack/react-router";
import { useVerdict } from "@/hooks/use-verdict";
import { QorumSeal } from "@/components/qorum/QorumSeal";
import { VerdictBandeau } from "@/components/qorum/VerdictBandeau";
import { VerdictHeadline } from "@/components/qorum/VerdictHeadline";
import { VerdictCondition } from "@/components/qorum/VerdictCondition";
import { ConfidenceLCD } from "@/components/qorum/ConfidenceLCD";
import { DissidenceCard } from "@/components/qorum/DissidenceCard";
import { VerdictSignature } from "@/components/qorum/VerdictSignature";
import { PublicVerdictBanner } from "@/components/qorum/PublicVerdictBanner";
import { PublicVerdictFooter } from "@/components/qorum/PublicVerdictFooter";
import { NanaeDisclaimer } from "@/components/qorum/NanaeDisclaimer";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/v/$id")({
  head: ({ params }) => ({
    meta: [
      { title: "Verdict partagé — Hōjin" },
      { name: "description", content: "Verdict rendu par le Conseil Hōjin — 7 voix, une direction. Partagé publiquement par son auteur." },
      ...socialMeta({
        title: "Verdict partagé — Hōjin",
        description: "Verdict rendu par le Conseil Hōjin — 7 voix, une direction. Partagé publiquement par son auteur.",
        url: `https://hojin.ai/v/${params.id}`,
        image: "memo",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: `https://hojin.ai/v/${params.id}` }],
  }),
  component: PublicVerdictPage,
});

export function PublicVerdictPage() {
  const { id } = Route.useParams();
  const { data, isLoading, error } = useVerdict(id);

  return (
    <div
      data-testid="public-verdict-page"
      style={{ background: "var(--paper)", minHeight: "100vh", position: "relative" }}
    >
      <PublicVerdictBanner />

      <section
        aria-label="Verdict du Conseil"
        data-rp="verdict-public-section"
        style={{
          maxWidth: 720,
          margin: "0 auto",
          padding: "48px 32px 0",
          position: "relative",
        }}
      >
        {data ? <QorumSeal idSuffix={id} /> : null}

        {isLoading && !data ? (
          <div
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontStyle: "italic",
              fontSize: 22,
              color: "var(--smoke)",
              textAlign: "center",
              padding: "120px 0",
            }}
          >
            En attente du verdict…
          </div>
        ) : error || !data ? (
          <div
            style={{
              textAlign: "center",
              padding: "120px 0",
            }}
          >
            <div
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: 11,
                color: "var(--leather)",
                letterSpacing: "3px",
                textTransform: "uppercase",
                marginBottom: 16,
              }}
            >
              Verdict introuvable
            </div>
            <p
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontStyle: "italic",
                fontSize: 22,
                color: "var(--smoke)",
                maxWidth: 480,
                margin: "0 auto",
              }}
            >
              Ce verdict n'est plus disponible.
            </p>
          </div>
        ) : (
          <>
            <VerdictBandeau saisineNumber={data.saisineNumber} mode={data.mode} />
            <VerdictHeadline
              saisineDate={data.saisineDate}
              saisineTime={data.saisineTime}
              durationMinutes={data.durationMinutes}
              question={data.question}
              headline={data.headline}
              subHeadline={data.subHeadline}
            />


            <div data-testid="verdict-conditions" style={{ marginBottom: 56 }}>
              {data.conditions.map((c, i) => (
                <VerdictCondition key={i} index={i + 1} html={c.text} />
              ))}
            </div>

            <ConfidenceLCD score={data.confidenceScore} label={data.confidenceLabel} />

            {data.dissidence ? <DissidenceCard dissidence={data.dissidence} /> : null}

            <VerdictSignature />
          </>
        )}
      </section>

      {data ? (
        <NanaeDisclaimer
          variant={(data as { branch?: string }).branch === "verif" ? "verif" : "default"}
        />
      ) : null}

      <PublicVerdictFooter />
    </div>
  );
}
